import React, { useState, useEffect, useContext } from "react";
import { AuthContext, API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Card, CardContent } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash2, ClipboardCheck, Link2, Copy, Ban, ChevronDown, ChevronRight, X, QrCode, Undo2, History, Pencil, Flag, MessageSquare } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";

const token = () => localStorage.getItem("token");
const authHeaders = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });

const downloadQR = (tokenId, subName) => {
  const canvas = document.getElementById(`qr-${tokenId}`);
  if (!canvas) return;
  const link = document.createElement("a");
  const slug = (subName || "subcontractor").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  link.download = `fitoff-qr-${slug}.png`;
  link.href = canvas.toDataURL("image/png");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const STATUS_STYLE = {
  not_started: "bg-slate-100 text-slate-600",
  complete: "bg-amber-100 text-amber-700",
  closed_out: "bg-green-100 text-green-700",
};
const STATUS_LABEL = { not_started: "Not Started", complete: "Awaiting Close-Out", closed_out: "Closed Out" };
const STATUS_ORDER = { not_started: 0, complete: 1, closed_out: 2 };
const fmtDate = (iso) => { if (!iso) return null; try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }); } catch { return null; } };
const fmtDateTime = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
const effProgress = (it) => (it.status === "complete" || it.status === "closed_out") ? 100 : (it.progress || 0);
const pctComplete = (items) => items.length ? Math.round(items.reduce((s, i) => s + effProgress(i), 0) / items.length) : 0;
const PROGRESS_OPTS = ["0", "25", "50", "75"];

function SiteTotals({ items, label = "Site totals" }) {
  const total = items.length;
  const pc = pctComplete(items);
  const aw = items.filter((i) => i.status === "complete").length;
  const co = items.filter((i) => i.status === "closed_out").length;
  const cell = "flex-1 min-w-[70px] rounded-lg px-3 py-2 text-center";
  return (
    <div className="mb-3 rounded-xl border bg-white p-3" data-testid="site-totals">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">
        <div className={`${cell} bg-slate-100`}><div className="text-lg font-bold text-slate-800">{total}</div><div className="text-[11px] text-slate-500">Total items</div></div>
        <div className={`${cell} bg-blue-50`}><div className="text-lg font-bold text-blue-700">{pc}%</div><div className="text-[11px] text-blue-700/80">Complete</div></div>
        <div className={`${cell} bg-amber-50`}><div className="text-lg font-bold text-amber-700">{aw}</div><div className="text-[11px] text-amber-700/80">Awaiting close-out</div></div>
        <div className={`${cell} bg-green-50`}><div className="text-lg font-bold text-green-700">{co}</div><div className="text-[11px] text-green-700/80">Closed out</div></div>
      </div>
      <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-blue-500 transition-all" style={{ width: `${pc}%` }} /></div>
    </div>
  );
}

function CountBadges({ items }) {
  const total = items.length;
  const pc = pctComplete(items);
  const aw = items.filter((i) => i.status === "complete").length;
  const co = items.filter((i) => i.status === "closed_out").length;
  const pill = "text-[10px] leading-none px-1.5 py-1 rounded-full font-semibold whitespace-nowrap";
  return (
    <span className="flex items-center gap-1 flex-wrap" data-testid="count-badges" onClick={(e) => e.stopPropagation()}>
      <span className={`${pill} bg-slate-200 text-slate-700`}>{total} total</span>
      <span className={`${pill} bg-blue-100 text-blue-700`}>{pc}% complete</span>
      <span className={`${pill} bg-amber-100 text-amber-700`}>{aw} awaiting</span>
      <span className={`${pill} bg-green-100 text-green-700`}>{co} closed out</span>
    </span>
  );
}

export default function IncompleteWork() {
  const { user } = useContext(AuthContext);
  const [tab, setTab] = useState("projects");
  const isAdmin = user?.role === "admin";
  const canDelete = user?.role === "admin" || user?.role === "company_owner";
  const canManage = ["admin", "company_owner", "site_manager"].includes(user?.role);

  if (!["admin", "company_owner", "site_manager"].includes(user?.role)) {
    return (
      <div className="p-8 max-w-lg mx-auto text-center" data-testid="iw-access-denied">
        <div className="p-3 rounded-full bg-red-100 w-14 h-14 mx-auto flex items-center justify-center mb-4">
          <ClipboardCheck className="w-7 h-7 text-red-500" />
        </div>
        <h1 className="text-xl font-bold text-slate-900">Access restricted</h1>
        <p className="text-slate-500 mt-2">Work In Progress is available to Admins, Owners and Site Managers only.</p>
      </div>
    );
  }

  // shared data
  const [sites, setSites] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [templates, setTemplates] = useState([]);

  const loadShared = async () => {
    const [s, t, tm] = await Promise.all([
      fetch(`${API}/sites`, { headers: authHeaders() }).then((r) => r.json()),
      fetch(`${API}/companies?active_only=true&company_type=subcontractor`, { headers: authHeaders() }).then((r) => r.json()),
      fetch(`${API}/fitoff-templates`, { headers: authHeaders() }).then((r) => r.json()),
    ]);
    setSites(Array.isArray(s) ? s : []);
    setSubcontractors(Array.isArray(t) ? t : []);
    setTemplates(Array.isArray(tm) ? tm : []);
  };
  useEffect(() => { loadShared(); }, []);

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 shadow">
          <ClipboardCheck className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="heading-primary text-2xl">Work In Progress</h1>
          <p className="text-sm text-slate-600">Subcontractor fit-off checklists, applied per project, tracked to close-out.</p>
        </div>
      </div>

      <div className="flex gap-2 mb-5 border-b">
        {["projects", "ready", "blocked", "templates"].map((tb) => (
          <button key={tb} data-testid={`iw-tab-${tb}`} onClick={() => setTab(tb)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === tb ? "border-orange-500 text-orange-600" : "border-transparent text-slate-500"}`}>
            {tb === "projects" ? "Projects" : tb === "ready" ? "Ready to Start" : tb === "blocked" ? "Notes / Blockers" : "Master Templates"}
          </button>
        ))}
      </div>

      {tab === "templates" ? (
        <TemplatesTab templates={templates} subcontractors={subcontractors} sites={sites} reload={loadShared} canDelete={canDelete} />
      ) : tab === "ready" ? (
        <ReadyToStartTab sites={sites} />
      ) : tab === "blocked" ? (
        <BlockedItemsTab sites={sites} canManage={canManage} />
      ) : (
        <ProjectsTab sites={sites} subcontractors={subcontractors} templates={templates} canDelete={canDelete} canManage={canManage} />
      )}
    </div>
  );
}

function ReadyToStartTab({ sites }) {
  const [projectId, setProjectId] = useState("");
  const [feed, setFeed] = useState(null);
  const [groupBy, setGroupBy] = useState("townhouse");
  const [openL1, setOpenL1] = useState({});
  const [openL2, setOpenL2] = useState({});
  const load = (pid) => {
    if (!pid) return;
    setFeed(null); setOpenL1({}); setOpenL2({});
    fetch(`${API}/fitoff/${pid}/ready-to-start`, { headers: authHeaders() })
      .then((r) => r.json()).then((d) => setFeed(d.items || []));
  };
  const unflag = async (id) => {
    const res = await fetch(`${API}/fitoff/${projectId}/items/unflag-ready`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify({ item_ids: [id] }) });
    if (res.ok) { toast.success("Removed"); load(projectId); }
  };
  const natCmp = (a, b) => (a || "").localeCompare(b || "", undefined, { numeric: true, sensitivity: "base" });
  const byTown = groupBy === "townhouse";
  const grouped = {};
  (feed || []).forEach((e) => {
    const l1 = byTown ? (e.dwelling || "Lot") : (e.subcontractor || "Unassigned");
    const l2 = byTown ? (e.subcontractor || "Unassigned") : (e.dwelling || "Lot");
    const l3 = e.room || "Other";
    grouped[l1] = grouped[l1] || {};
    grouped[l1][l2] = grouped[l1][l2] || {};
    (grouped[l1][l2][l3] = grouped[l1][l2][l3] || []).push(e);
  });
  const collect = (node) => Array.isArray(node) ? node : Object.values(node).reduce((a, c) => a.concat(collect(c)), []);
  const l1Keys = Object.keys(grouped).sort(natCmp);
  const total = (feed || []).length;
  const CountPill = ({ n }) => <span className="text-[10px] px-1.5 py-1 rounded-full bg-emerald-100 text-emerald-700 font-semibold whitespace-nowrap">{n} item{n !== 1 ? "s" : ""}</span>;

  return (
    <div>
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <div className="max-w-xs w-full sm:w-64">
          <Select value={projectId} onValueChange={(v) => { setProjectId(v); load(v); }}>
            <SelectTrigger data-testid="ready-project-select"><SelectValue placeholder="Select a project" /></SelectTrigger>
            <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {projectId && total > 0 && (
          <div className="flex items-center gap-1 ml-auto">
            <span className="text-xs text-slate-500 mr-1">Group by</span>
            <div className="inline-flex rounded-md border overflow-hidden" data-testid="ready-groupby-toggle">
              <button className={`px-3 py-1.5 text-xs font-medium ${byTown ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`}
                onClick={() => { setGroupBy("townhouse"); setOpenL1({}); setOpenL2({}); }} data-testid="ready-groupby-townhouse">Townhouse</button>
              <button className={`px-3 py-1.5 text-xs font-medium ${!byTown ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`}
                onClick={() => { setGroupBy("subcontractor"); setOpenL1({}); setOpenL2({}); }} data-testid="ready-groupby-subcontractor">Subcontractor</button>
            </div>
          </div>
        )}
      </div>
      {!projectId && <p className="text-sm text-slate-400">Select a project to see trades flagged as ready to start.</p>}
      {projectId && feed && total === 0 && <p className="text-sm text-slate-400">No trades are currently flagged as ready to start (and still at 0%).</p>}
      {projectId && total > 0 && (
        <div className="mb-3 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm font-semibold text-emerald-800" data-testid="ready-total-banner">
          {total} item{total !== 1 ? "s" : ""} ready to start
        </div>
      )}
      {l1Keys.map((l1) => {
        const l1Open = !!openL1[l1];
        const l2Keys = Object.keys(grouped[l1]).sort(natCmp);
        return (
          <Card key={l1} className="mb-3" data-testid={`ready-l1-${l1}`}>
            <CardContent className="p-3">
              <button className="w-full flex items-center gap-2 font-bold text-slate-800 text-left"
                onClick={() => setOpenL1({ ...openL1, [l1]: !l1Open })} data-testid={`ready-l1-toggle-${l1}`}>
                {l1Open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                <span className="flex-1 min-w-0 truncate">{l1}</span>
                <CountPill n={collect(grouped[l1]).length} />
              </button>
              {l1Open && (
                <div className="mt-2 space-y-2">
                  {l2Keys.map((l2) => {
                    const sk = `${l1}||${l2}`;
                    const l2Open = !!openL2[sk];
                    const l3Keys = Object.keys(grouped[l1][l2]).sort(natCmp);
                    return (
                      <div key={sk} className="border rounded-md">
                        <button className="w-full flex items-center gap-2 px-2 py-1.5 text-left bg-slate-50 rounded-t-md"
                          onClick={() => setOpenL2({ ...openL2, [sk]: !l2Open })} data-testid={`ready-l2-toggle-${sk}`}>
                          {l2Open ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
                          <span className="flex-1 min-w-0 truncate text-sm font-semibold text-slate-700">{l2}</span>
                          <CountPill n={collect(grouped[l1][l2]).length} />
                        </button>
                        {l2Open && (
                          <div className="p-2 space-y-2">
                            {l3Keys.map((l3) => (
                              <div key={l3}>
                                <div className="flex items-center gap-2 text-xs font-semibold text-slate-600 mb-1">
                                  <span className="flex-1 min-w-0 truncate">{l3}</span>
                                  <CountPill n={grouped[l1][l2][l3].length} />
                                </div>
                                {grouped[l1][l2][l3].map((e) => (
                                  <div key={e.id} className="flex flex-wrap items-center gap-2 border-t pt-2 text-sm" data-testid={`ready-item-${e.id}`}>
                                    <span className="flex-1 min-w-[55%]">
                                      <span className="font-medium">{e.item_name}</span>
                                      {e.note && <span className="block text-xs text-emerald-700">“{e.note}”</span>}
                                      <span className="block text-xs text-slate-400">flagged by {e.flagged_by} · {fmtDateTime(e.flagged_at)}</span>
                                    </span>
                                    <Button size="sm" variant="outline" data-testid={`ready-unflag-${e.id}`} onClick={() => unflag(e.id)}>Remove flag</Button>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

/* ============================= BLOCKED / NOTES TAB ============================= */
function BlockedItemsTab({ sites, canManage }) {
  const [projectId, setProjectId] = useState("");
  const [feed, setFeed] = useState(null);
  const [text, setText] = useState({});
  const [groupBy, setGroupBy] = useState("dwelling");
  const load = (pid) => {
    if (!pid) return;
    setFeed(null);
    fetch(`${API}/fitoff/${pid}/with-notes`, { headers: authHeaders() })
      .then((r) => r.json()).then((d) => setFeed(d.items || []));
  };
  const addNote = async (id, t) => {
    const res = await fetch(`${API}/fitoff/${projectId}/items/${id}/notes`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ text: t }) });
    if (res.ok) { toast.success("Note added"); setText({ ...text, [id]: "" }); load(projectId); }
    else { const e = await res.json(); toast.error(e.detail || "Failed"); }
  };
  const delNote = async (id, nid) => {
    const res = await fetch(`${API}/fitoff/${projectId}/items/${id}/notes/${nid}`, { method: "DELETE", headers: authHeaders() });
    if (res.ok) { toast.success("Note deleted"); load(projectId); }
  };
  const natCmp = (a, b) => (a || "").localeCompare(b || "", undefined, { numeric: true, sensitivity: "base" });
  const byDwell = groupBy === "dwelling";
  const grp = {};
  (feed || []).forEach((e) => { const k = byDwell ? (e.dwelling || "Lot") : (e.subcontractor || "Unassigned"); (grp[k] = grp[k] || []).push(e); });
  const total = (feed || []).length;
  return (
    <div>
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <div className="max-w-xs w-full sm:w-64">
          <Select value={projectId} onValueChange={(v) => { setProjectId(v); load(v); }}>
            <SelectTrigger data-testid="blocked-project-select"><SelectValue placeholder="Select a project" /></SelectTrigger>
            <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {projectId && total > 0 && (
          <div className="flex items-center gap-1 ml-auto">
            <span className="text-xs text-slate-500 mr-1">Group by</span>
            <div className="inline-flex rounded-md border overflow-hidden" data-testid="blocked-groupby-toggle">
              <button className={`px-3 py-1.5 text-xs font-medium ${byDwell ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`}
                onClick={() => setGroupBy("dwelling")} data-testid="blocked-groupby-dwelling">Dwelling</button>
              <button className={`px-3 py-1.5 text-xs font-medium ${!byDwell ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`}
                onClick={() => setGroupBy("subcontractor")} data-testid="blocked-groupby-subcontractor">Subcontractor</button>
            </div>
          </div>
        )}
      </div>
      {!projectId && <p className="text-sm text-slate-400">Select a project to see every item that currently has notes.</p>}
      {projectId && feed && total === 0 && <p className="text-sm text-slate-400">No items have notes right now.</p>}
      {projectId && total > 0 && (
        <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm font-semibold text-amber-800" data-testid="blocked-total-banner">
          {total} item{total !== 1 ? "s" : ""} with notes
        </div>
      )}
      {Object.keys(grp).sort(natCmp).map((gk) => (
        <Card key={gk} className="mb-3" data-testid={`blocked-group-${gk}`}>
          <CardContent className="p-3">
            <h3 className="font-bold text-slate-800 mb-2">{gk}</h3>
            <div className="space-y-3">
              {grp[gk].map((e) => (
                <div key={e.id} className="border rounded-md p-2" data-testid={`blocked-item-${e.id}`}>
                  <p className="text-sm font-semibold text-slate-800">{e.item_name}
                    <span className="font-normal text-slate-500"> · {byDwell ? `${e.room} · ${e.subcontractor}` : `${e.dwelling} · ${e.room}`}</span></p>
                  <div className="space-y-1 mt-1">
                    {(e.notes || []).map((n) => (
                      <div key={n.id} className={`border rounded p-2 ${n.role === "manager" ? "border-blue-300 bg-blue-50" : "border-emerald-300 bg-emerald-50"}`}>
                        <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">{n.text}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${n.role === "manager" ? "bg-blue-200 text-blue-800" : "bg-emerald-200 text-emerald-800"}`}>{n.role === "manager" ? "Site Manager" : "Subcontractor"}</span>
                          <span className="text-xs text-slate-500 flex-1 min-w-0 truncate">{n.by} · {fmtDateTime(n.at)}{n.edited_at ? " (edited)" : ""}</span>
                          {canManage && <button className="text-red-500" data-testid={`blocked-note-del-${n.id}`} onClick={() => delNote(e.id, n.id)}><Trash2 className="w-3.5 h-3.5" /></button>}
                        </div>
                      </div>
                    ))}
                  </div>
                  {e.status === "not_started" && (
                    <div className="flex items-center gap-2 mt-2">
                      <Input maxLength={250} className="h-8 text-sm" placeholder="Reply / add a note…" value={text[e.id] || ""}
                        onChange={(ev) => setText({ ...text, [e.id]: ev.target.value })} data-testid={`blocked-note-input-${e.id}`} />
                      <Button size="sm" data-testid={`blocked-note-submit-${e.id}`} disabled={!(text[e.id] || "").trim()} onClick={() => addNote(e.id, (text[e.id] || "").trim())}>Add</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ============================= TEMPLATES TAB ============================= */
function TemplatesTab({ templates, subcontractors, sites, reload, canDelete }) {
  const [editing, setEditing] = useState(null); // template object or {new:true}
  const siteName = (id) => sites.find((s) => s.id === id)?.name || "Unknown site";

  const del = async (id) => {
    if (!window.confirm("Delete this master template?")) return;
    const res = await fetch(`${API}/fitoff-templates/${id}`, { method: "DELETE", headers: authHeaders() });
    if (res.ok) { toast.success("Template deleted"); reload(); }
    else { const e = await res.json(); toast.error(e.detail || "Delete failed"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-500">Each template belongs to one site and is applied to that site's townhouses.</p>
        <Button data-testid="new-template-btn" onClick={() => setEditing({ new: true, site_id: "", name: "", areas: [] })}>
          <Plus className="w-4 h-4 mr-1" /> New Template
        </Button>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {templates.length === 0 && <p className="text-sm text-slate-400">No master templates yet.</p>}
        {templates.map((t) => (
          <Card key={t.id} data-testid={`template-card-${t.id}`}>
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-slate-900">{t.name}</h3>
                  <p className="text-xs text-orange-600 font-medium mt-0.5">{siteName(t.site_id)}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {(t.areas || []).length} area(s) · {(t.areas || []).reduce((a, ar) => a + (ar.subcontractors || []).length, 0)} subcontractor allocation(s)
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => setEditing(t)}>Edit</Button>
                  {canDelete && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => del(t.id)}><Trash2 className="w-4 h-4" /></Button>}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {editing && <TemplateEditor initial={editing} subcontractors={subcontractors} sites={sites} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
}

function TemplateEditor({ initial, subcontractors, sites, onClose, onSaved }) {
  const [siteId, setSiteId] = useState(initial.site_id || "");
  const [name, setName] = useState(initial.name || "");
  const [areas, setAreas] = useState(JSON.parse(JSON.stringify(initial.areas || [])));
  const [allSubcontractors, setAllSubcontractors] = useState(subcontractors);
  const [newSubcontractor, setNewSubcontractor] = useState({});
  const [subSearch, setSubSearch] = useState({});
  const [openAreas, setOpenAreas] = useState({});

  const toggleArea = (ai) => setOpenAreas((o) => ({ ...o, [ai]: !o[ai] }));
  const addArea = () => { const ni = areas.length; setAreas([...areas, { name: "", repeatable: false, subcontractors: [] }]); setOpenAreas({ [ni]: true }); };
  const setArea = (ai, k, v) => { const c = [...areas]; c[ai][k] = v; setAreas(c); };
  const delArea = (ai) => { const c = [...areas]; c.splice(ai, 1); setAreas(c); setOpenAreas({}); };
  const isAllocated = (ai, tid) => areas[ai].subcontractors.some((t) => t.subcontractor_id === tid);
  const toggleSubcontractor = (ai, tr) => {
    const c = [...areas];
    const idx = c[ai].subcontractors.findIndex((t) => t.subcontractor_id === tr.id);
    if (idx >= 0) c[ai].subcontractors.splice(idx, 1);
    else c[ai].subcontractors.push({ subcontractor_id: tr.id, subcontractor_name: tr.name, items: [] });
    setAreas(c);
  };
  const addItem = (ai, tj, val) => { if (!val.trim()) return; const c = [...areas]; c[ai].subcontractors[tj].items.push(val.trim()); setAreas(c); };
  const delItem = (ai, tj, ii) => { const c = [...areas]; c[ai].subcontractors[tj].items.splice(ii, 1); setAreas(c); };

  const allocateSubcontractor = (ai, t) => {
    const c = [...areas];
    if (!c[ai].subcontractors.some((x) => x.subcontractor_id === t.id)) c[ai].subcontractors.push({ subcontractor_id: t.id, subcontractor_name: t.name, items: [] });
    setAreas(c);
  };

  // Subcontractors allocated to the selected site.
  const siteSubs = allSubcontractors.filter((t) => (t.site_ids || []).includes(siteId));

  const createSubcontractor = async (ai) => {
    const nm = (newSubcontractor[ai] || "").trim();
    if (!nm) return;
    if (!siteId) return toast.error("Select a site first");
    // Already in the loaded list -> just select it (no duplicate error).
    const existing = allSubcontractors.find((t) => (t.name || "").toLowerCase() === nm.toLowerCase());
    if (existing) {
      allocateSubcontractor(ai, existing);
      setNewSubcontractor({ ...newSubcontractor, [ai]: "" });
      toast.success(`"${existing.name}" selected`);
      return;
    }
    const res = await fetch(`${API}/companies`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({ name: nm, company_type: "subcontractor", site_ids: [siteId] }),
    });
    if (res.ok) {
      const t = await res.json();
      setAllSubcontractors((prev) => [...prev, t]);
      allocateSubcontractor(ai, t);
      setNewSubcontractor({ ...newSubcontractor, [ai]: "" });
      toast.success(`Subcontractor "${t.name}" added to this site`);
      return;
    }
    // Duplicate (already exists) -> refetch subcontractors and select it.
    const list = await fetch(`${API}/companies?active_only=true&company_type=subcontractor`, { headers: authHeaders() }).then((r) => r.json()).catch(() => []);
    const found = Array.isArray(list) ? list.find((t) => (t.name || "").toLowerCase() === nm.toLowerCase()) : null;
    if (found) {
      setAllSubcontractors(list);
      allocateSubcontractor(ai, found);
      setNewSubcontractor({ ...newSubcontractor, [ai]: "" });
      toast.success(`"${found.name}" selected`);
    } else {
      const e = await res.json().catch(() => ({}));
      toast.error(e.detail || "Could not add subcontractor");
    }
  };

  const save = async () => {
    if (!siteId) return toast.error("Select a site");
    if (!name.trim()) return toast.error("Template name required");
    const body = { site_id: siteId, name, areas };
    const url = initial.new ? `${API}/fitoff-templates` : `${API}/fitoff-templates/${initial.id}`;
    const res = await fetch(url, { method: initial.new ? "POST" : "PUT", headers: authHeaders(), body: JSON.stringify(body) });
    if (res.ok) { toast.success("Template saved"); onSaved(); }
    else toast.error("Save failed");
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto" data-testid="template-editor">
        <DialogHeader><DialogTitle>{initial.new ? "New" : "Edit"} Master Template</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Site</Label>
            <Select value={siteId} onValueChange={setSiteId}>
              <SelectTrigger data-testid="template-site-select"><SelectValue placeholder="Select a site" /></SelectTrigger>
              <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Template Name</Label>
            <Input data-testid="template-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Standard Residential Fit-Off" />
          </div>

          <div>
            <Label>Areas / Rooms</Label>
            {areas.length === 0 && <p className="text-xs text-slate-400 mt-1">Add an area/room, allocate the subcontractor(s) that work there, then list their items.</p>}

            {areas.map((area, ai) => {
              const open = !!openAreas[ai];
              return (
              <div key={ai} className="border rounded-lg bg-slate-50 mt-2" data-testid={`area-block-${ai}`}>
                <button type="button" className="w-full flex items-center gap-2 p-3 text-left" onClick={() => toggleArea(ai)} data-testid={`area-toggle-${ai}`}>
                  {open ? <ChevronDown className="w-4 h-4 shrink-0 text-slate-500" /> : <ChevronRight className="w-4 h-4 shrink-0 text-slate-500" />}
                  <span className="flex-1 min-w-0 truncate font-medium text-sm text-slate-800">{area.name || <span className="text-slate-400 italic">New room / area</span>}</span>
                  {area.repeatable && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 whitespace-nowrap">repeatable</span>}
                  <span className="text-xs text-slate-400 whitespace-nowrap">{area.subcontractors.length} sub(s)</span>
                  <span role="button" className="text-red-600 p-1 -m-1" onClick={(e) => { e.stopPropagation(); delArea(ai); }} data-testid={`area-del-${ai}`}><Trash2 className="w-4 h-4" /></span>
                </button>
                {open && (
                <div className="px-3 pb-3">
                <div className="flex items-center gap-2 mb-2">
                  <Input className="h-9" data-testid={`area-name-${ai}`} placeholder="Area / Room (e.g. Kitchen, Bedroom)" value={area.name} onChange={(e) => setArea(ai, "name", e.target.value)} />
                  <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                    <Checkbox checked={area.repeatable} onCheckedChange={(v) => setArea(ai, "repeatable", !!v)} data-testid={`area-repeat-${ai}`} /> repeatable
                  </label>
                </div>

                <p className="text-xs font-medium text-slate-600 mb-1">Allocate subcontractors (tick to select one or more):</p>
                {!siteId && <span className="text-xs text-slate-400">Select a site above to see its subcontractors.</span>}
                {siteId && (
                  <>
                    {siteSubs.length > 8 && (
                      <Input className="h-8 text-sm mb-1" data-testid={`area-${ai}-sub-search`} placeholder="Search subcontractors..."
                        value={subSearch[ai] || ""} onChange={(e) => setSubSearch({ ...subSearch, [ai]: e.target.value })} />
                    )}
                    <div className="border rounded-md bg-white max-h-44 overflow-y-auto p-2 space-y-1 mb-2">
                      {siteSubs.length === 0 && <span className="text-xs text-slate-400">No subcontractors on this site yet — add one below.</span>}
                      {siteSubs
                        .filter((t) => (t.name || "").toLowerCase().includes((subSearch[ai] || "").toLowerCase()))
                        .map((t) => (
                          <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                            <Checkbox checked={isAllocated(ai, t.id)} onCheckedChange={() => toggleSubcontractor(ai, t)} data-testid={`area-${ai}-subcontractor-${t.id}`} />
                            <span className="truncate">{t.name}</span>
                          </label>
                        ))}
                      {siteSubs.length > 0 && siteSubs.filter((t) => (t.name || "").toLowerCase().includes((subSearch[ai] || "").toLowerCase())).length === 0 && (
                        <span className="text-xs text-slate-400">No matches.</span>
                      )}
                    </div>
                  </>
                )}
                <div className="flex gap-1 mb-3">
                  <Input className="h-7 text-xs" data-testid={`area-${ai}-newsubcontractor`} placeholder="+ new subcontractor (adds to this site)" value={newSubcontractor[ai] || ""}
                    onChange={(e) => setNewSubcontractor({ ...newSubcontractor, [ai]: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createSubcontractor(ai); } }} />
                  <Button size="sm" variant="outline" className="h-7" data-testid={`area-${ai}-addsubcontractor-btn`} onClick={() => createSubcontractor(ai)}>Add</Button>
                </div>

                {area.subcontractors.map((tr, tj) => (
                  <div key={tj} className="bg-white border rounded p-2 mb-2" data-testid={`area-${ai}-alloc-${tr.subcontractor_id}`}>
                    <div className="flex justify-between items-center mb-1">
                      <h4 className="font-semibold text-sm text-slate-800">{tr.subcontractor_name}</h4>
                      <Button size="sm" variant="ghost" className="text-red-600 h-6" onClick={() => toggleSubcontractor(ai, { id: tr.subcontractor_id, name: tr.subcontractor_name })}><X className="w-4 h-4" /></Button>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {tr.items.map((it, ii) => (
                        <span key={ii} className="inline-flex items-center gap-1 bg-slate-100 text-xs rounded-full px-2 py-0.5">
                          {it}<button onClick={() => delItem(ai, tj, ii)}><X className="w-3 h-3" /></button>
                        </span>
                      ))}
                    </div>
                    <ItemAdder onAdd={(v) => addItem(ai, tj, v)} />
                  </div>
                ))}
                </div>
                )}
              </div>
              );
            })}
            <Button size="sm" variant="outline" className="mt-3 w-full" data-testid="add-area-btn" onClick={addArea}><Plus className="w-4 h-4 mr-1" />Add Area/Room</Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="save-template-btn" onClick={save}>Save Template</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ItemAdder({ onAdd }) {
  const [v, setV] = useState("");
  return (
    <div className="flex gap-1">
      <Input className="h-7 text-xs" placeholder="+ item (e.g. Kitchen Sink)" value={v}
        onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { onAdd(v); setV(""); } }} />
      <Button size="sm" variant="outline" className="h-7" onClick={() => { onAdd(v); setV(""); }}>Add</Button>
    </div>
  );
}

/* ============================= PROJECTS TAB ============================= */
function ProjectsTab({ sites, subcontractors, templates, canDelete, canManage }) {
  const [projectId, setProjectId] = useState("");
  const [dwellings, setDwellings] = useState([]);
  const [data, setData] = useState({ project_template: null, townhouses: [], items: [] });
  const [tokens, setTokens] = useState([]);
  const [showApply, setShowApply] = useState(false);
  const [showAdHoc, setShowAdHoc] = useState(false);
  const [showTokens, setShowTokens] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [roomDelete, setRoomDelete] = useState(null); // {room, townhouseId, dwellingLabel}
  const [roomEdit, setRoomEdit] = useState(null); // {room, townhouseId, dwellingLabel, newName}
  const [readyDialog, setReadyDialog] = useState(null); // {townhouseId, room, excludeSubId, dwellingLabel}
  const flagReady = async (itemIds, note) => {
    const res = await fetch(`${API}/fitoff/${projectId}/items/flag-ready`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify({ item_ids: itemIds, note }) });
    if (res.ok) { const d = await res.json(); toast.success(`Flagged ${d.flagged} item(s) ready to start`); setReadyDialog(null); load(projectId); }
    else { const e = await res.json(); toast.error(e.detail || "Failed"); }
  };
  const closeOutOne = async (it) => {
    await closeOut([it.id]);
    setReadyDialog({ townhouseId: it.townhouse_id, room: it.room_instance_label || it.room_name, excludeSubId: it.subcontractor_id, dwellingLabel: dwName(it.townhouse_id) });
  };
  const [notesItem, setNotesItem] = useState(null); // item object whose notes dialog is open
  const addNote = async (itemId, text) => {
    const res = await fetch(`${API}/fitoff/${projectId}/items/${itemId}/notes`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ text }) });
    if (res.ok) { toast.success("Note added"); await load(projectId); return true; }
    const e = await res.json(); toast.error(e.detail || "Failed"); return false;
  };
  const editNote = async (itemId, noteId, text) => {
    const res = await fetch(`${API}/fitoff/${projectId}/items/${itemId}/notes/${noteId}`, { method: "PUT", headers: authHeaders(), body: JSON.stringify({ text }) });
    if (res.ok) { toast.success("Note updated"); await load(projectId); return true; }
    const e = await res.json(); toast.error(e.detail || "Failed"); return false;
  };
  const deleteNote = async (itemId, noteId) => {
    const res = await fetch(`${API}/fitoff/${projectId}/items/${itemId}/notes/${noteId}`, { method: "DELETE", headers: authHeaders() });
    if (res.ok) { toast.success("Note deleted"); load(projectId); }
  };
  const deleteRoom = async (scopeAll) => {
    const body = { room: roomDelete.room };
    if (!scopeAll) body.townhouse_id = roomDelete.townhouseId;
    const res = await fetch(`${API}/fitoff/${projectId}/delete-room`, { method: "POST", headers: authHeaders(), body: JSON.stringify(body) });
    if (res.ok) { const d = await res.json(); toast.success(`Deleted ${d.deleted} item(s)`); setRoomDelete(null); load(projectId); }
    else { const e = await res.json(); toast.error(e.detail || "Failed"); }
  };
  const renameRoom = async (scopeAll) => {
    const body = { old_room: roomEdit.room, new_room: roomEdit.newName };
    if (!scopeAll) body.townhouse_id = roomEdit.townhouseId;
    const res = await fetch(`${API}/fitoff/${projectId}/rename-room`, { method: "POST", headers: authHeaders(), body: JSON.stringify(body) });
    if (res.ok) { const d = await res.json(); toast.success(`Renamed ${d.updated} item(s)`); setRoomEdit(null); load(projectId); }
    else { const e = await res.json(); toast.error(e.detail || "Failed"); }
  };
  const [expanded, setExpanded] = useState({});
  const [expandedSub, setExpandedSub] = useState({});
  const [expandedRoom, setExpandedRoom] = useState({});
  const [selected, setSelected] = useState({});
  const [openHistory, setOpenHistory] = useState({});
  const [groupBy, setGroupBy] = useState("townhouse");
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "outstanding"
  const changeGroupBy = (mode) => { setGroupBy(mode); setExpanded({}); setExpandedSub({}); setExpandedRoom({}); };
  const isVisible = (it) => statusFilter === "all" ? true : it.status === "not_started";
  const anyVisible = (arr) => statusFilter === "all" ? arr.length > 0 : arr.some(isVisible);

  const load = async (pid) => {
    if (!pid) return;
    const [dw, d, tk] = await Promise.all([
      fetch(`${API}/dwellings?site_id=${pid}`, { headers: authHeaders() }).then((r) => r.json()).catch(() => []),
      fetch(`${API}/fitoff/${pid}`, { headers: authHeaders() }).then((r) => r.json()),
      fetch(`${API}/fitoff/${pid}/subcontractor-tokens`, { headers: authHeaders() }).then((r) => r.json()),
    ]);
    setDwellings(Array.isArray(dw) ? dw : []);
    setData(d || { townhouses: [], items: [] });
    setTokens(Array.isArray(tk) ? tk : []);
  };
  useEffect(() => { if (projectId) load(projectId); }, [projectId]);

  const items = data.items || [];
  const awaiting = items.filter((i) => i.status === "complete");
  const dwName = (id) => dwellings.find((d) => d.id === id)?.dwelling_number || "Lot";
  const sortNat = (a, b) => (a || "").localeCompare(b || "", undefined, { numeric: true, sensitivity: "base" });
  // Three-level grouping. Dwelling mode: Townhouse -> Room -> Subcontractor.
  // Subcontractor mode: Subcontractor -> Townhouse -> Room.
  const byTown = groupBy === "townhouse";
  const roomKey = (i) => i.room_instance_label || i.room_name || "Other";
  const lvl = byTown
    ? { k1: (i) => i.townhouse_id, k2: roomKey, k3: (i) => i.subcontractor_name || "Unassigned",
        lab1: dwName, lab2: (x) => x, lab3: (x) => x }
    : { k1: (i) => i.subcontractor_name || "Unassigned", k2: (i) => i.townhouse_id, k3: roomKey,
        lab1: (x) => x, lab2: dwName, lab3: (x) => x };
  const collect = (node) => Array.isArray(node) ? node : Object.values(node).reduce((a, c) => a.concat(collect(c)), []);
  const grouped = {};
  items.forEach((i) => {
    const a = lvl.k1(i), b = lvl.k2(i), c = lvl.k3(i);
    grouped[a] = grouped[a] || {};
    grouped[a][b] = grouped[a][b] || {};
    (grouped[a][b][c] = grouped[a][b][c] || []).push(i);
  });
  const l1Keys = Object.keys(grouped).sort((a, b) => sortNat(lvl.lab1(a), lvl.lab1(b)));

  const closeOut = async (ids) => {
    const res = await fetch(`${API}/fitoff/${projectId}/items/close-out-bulk`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify({ item_ids: ids }) });
    if (res.ok) { toast.success("Closed out"); setSelected({}); load(projectId); }
  };
  const markComplete = async (id) => {
    const res = await fetch(`${API}/fitoff/${projectId}/items/${id}/complete`, { method: "PUT", headers: authHeaders() });
    if (res.ok) { toast.success("Marked complete"); load(projectId); } else { const e = await res.json(); toast.error(e.detail || "Failed"); }
  };
  const setProgress = async (id, pct) => {
    if (pct === "100") return markComplete(id);
    const res = await fetch(`${API}/fitoff/${projectId}/items/${id}/progress`, { method: "PUT", headers: authHeaders(), body: JSON.stringify({ progress: Number(pct) }) });
    if (res.ok) { load(projectId); } else { const e = await res.json(); toast.error(e.detail || "Failed"); }
  };
  const undoComplete = async (id) => {
    const res = await fetch(`${API}/fitoff/${projectId}/items/${id}/undo-complete`, { method: "PUT", headers: authHeaders() });
    if (res.ok) { toast.success("Moved back to Not Started"); load(projectId); } else { const e = await res.json(); toast.error(e.detail || "Failed"); }
  };
  const delItem = async (id) => {
    const res = await fetch(`${API}/fitoff/${projectId}/items/${id}`, { method: "DELETE", headers: authHeaders() });
    if (res.ok) { toast.success("Item deleted"); load(projectId); } else { const e = await res.json(); toast.error(e.detail || "Delete failed"); }
  };
  const selIds = Object.keys(selected).filter((k) => selected[k]);

  return (
    <div>
      <div className="flex flex-wrap gap-2 items-end mb-4">
        <div className="w-full sm:w-auto">
          <Label>Project</Label>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-full sm:w-64" data-testid="iw-project-select"><SelectValue placeholder="Select a project" /></SelectTrigger>
            <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {projectId && (
          <>
            <Button data-testid="apply-template-btn" onClick={() => setShowApply(true)}>Apply Template</Button>
            <Button variant="outline" data-testid="add-item-btn" onClick={() => setShowAdHoc(true)}><Plus className="w-4 h-4 mr-1" />Add Item</Button>
            <Button variant="outline" data-testid="manage-tokens-btn" onClick={() => setShowTokens(true)}><Link2 className="w-4 h-4 mr-1" />Subcontractor Links</Button>
            <Button variant="outline" data-testid="activity-btn" onClick={() => setShowActivity(true)}><History className="w-4 h-4 mr-1" />Activity</Button>
            <div className="flex flex-wrap items-center gap-3 ml-auto">
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-500 mr-1">Show</span>
                <div className="inline-flex rounded-md border overflow-hidden" data-testid="status-filter-toggle">
                  <button className={`px-3 py-1.5 text-xs font-medium ${statusFilter === "all" ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`}
                    onClick={() => setStatusFilter("all")} data-testid="status-filter-all">All items</button>
                  <button className={`px-3 py-1.5 text-xs font-medium ${statusFilter === "outstanding" ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`}
                    onClick={() => setStatusFilter("outstanding")} data-testid="status-filter-outstanding">Outstanding only</button>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-500 mr-1">Group by</span>
                <div className="inline-flex rounded-md border overflow-hidden" data-testid="groupby-toggle">
                  <button className={`px-3 py-1.5 text-xs font-medium ${byTown ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`}
                    onClick={() => changeGroupBy("townhouse")} data-testid="groupby-townhouse">Dwelling</button>
                  <button className={`px-3 py-1.5 text-xs font-medium ${!byTown ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`}
                    onClick={() => changeGroupBy("subcontractor")} data-testid="groupby-subcontractor">Subcontractor</button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {!projectId && <p className="text-sm text-slate-400">Select a project to view its fit-off items.</p>}

      {projectId && awaiting.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex flex-wrap gap-2 justify-between items-center">
          <span className="text-sm font-medium text-amber-800">{awaiting.length} item(s) awaiting close-out</span>
          {selIds.length > 0 && <Button size="sm" data-testid="bulk-closeout-btn" onClick={() => closeOut(selIds)}>Close Out Selected ({selIds.length})</Button>}
        </div>
      )}

      {projectId && l1Keys.length > 0 && <SiteTotals items={items} label="Whole-site totals" />}

      {projectId && l1Keys.length === 0 && (
        <p className="text-sm text-slate-400">No fit-off items yet. Use "Apply Template" to generate a checklist.</p>
      )}

      {projectId && l1Keys.length > 0 && !anyVisible(items) && (
        <p className="text-sm text-slate-400">No outstanding items — everything is completed or closed out. Switch to "All items" to see them.</p>
      )}

      {l1Keys.map((l1) => {
        const l1Open = !!expanded[l1];
        const l2Keys = Object.keys(grouped[l1]).sort((a, b) => sortNat(lvl.lab2(a), lvl.lab2(b)));
        const l1Items = collect(grouped[l1]);
        if (!anyVisible(l1Items)) return null;
        return (
          <Card key={l1} className="mb-3">
            <CardContent className="p-3">
              <button className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 font-bold text-slate-800 w-full text-left"
                onClick={() => setExpanded({ ...expanded, [l1]: !l1Open })} data-testid={`th-toggle-${l1}`}>
                <span className="flex items-center gap-2 w-full sm:flex-1 min-w-0">
                  {l1Open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                  <span className="min-w-0 truncate">{lvl.lab1(l1)}</span>
                </span>
                <span className="pl-6 sm:pl-0"><CountBadges items={l1Items} /></span>
              </button>
              {l1Open && (
                <div className="mt-2 space-y-2">
                  {l2Keys.map((l2) => {
                    const sk = `${l1}||${l2}`;
                    const l2Open = !!expandedSub[sk];
                    const l3Keys = Object.keys(grouped[l1][l2]).sort((a, b) => sortNat(lvl.lab3(a), lvl.lab3(b)));
                    const l2Items = collect(grouped[l1][l2]);
                    if (!anyVisible(l2Items)) return null;
                    return (
                      <div key={sk} className="border rounded-md">
                        <button className="w-full flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 px-2 py-1.5 text-left bg-slate-50 rounded-t-md"
                          onClick={() => setExpandedSub({ ...expandedSub, [sk]: !l2Open })} data-testid={`sub-toggle-${sk}`}>
                          <span className="flex items-center gap-2 w-full sm:flex-1 min-w-0">
                            {l2Open ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
                            <span className="min-w-0 truncate text-sm font-semibold text-slate-700">{lvl.lab2(l2)}</span>
                          </span>
                          <span className="pl-6 sm:pl-0 flex items-center gap-1">
                            <CountBadges items={l2Items} />
                            {canManage && byTown && (
                              <span role="button" data-testid={`flag-room-${sk}`} className="text-emerald-600 p-1 -m-1 shrink-0"
                                onClick={(e) => { e.stopPropagation(); setReadyDialog({ townhouseId: l1, room: l2, dwellingLabel: lvl.lab1(l1) }); }}>
                                <Flag className="w-4 h-4" />
                              </span>
                            )}
                            {canManage && byTown && (
                              <span role="button" data-testid={`edit-room-${sk}`} className="text-slate-500 p-1 -m-1 shrink-0"
                                onClick={(e) => { e.stopPropagation(); setRoomEdit({ room: l2, townhouseId: l1, dwellingLabel: lvl.lab1(l1), newName: l2 }); }}>
                                <Pencil className="w-4 h-4" />
                              </span>
                            )}
                            {canManage && byTown && (
                              <span role="button" data-testid={`del-room-${sk}`} className="text-red-500 p-1 -m-1 shrink-0"
                                onClick={(e) => { e.stopPropagation(); setRoomDelete({ room: l2, townhouseId: l1, dwellingLabel: lvl.lab1(l1) }); }}>
                                <Trash2 className="w-4 h-4" />
                              </span>
                            )}
                          </span>
                        </button>
                        {l2Open && (
                          <div className="p-2 space-y-1">
                            {l3Keys.map((l3) => {
                                const l3Items = grouped[l1][l2][l3];
                                if (!anyVisible(l3Items)) return null;
                                const rk = `${sk}||${l3}`;
                                const l3Open = !!expandedRoom[rk];
                                return (
                                  <div key={rk} className="border rounded">
                                    <button className="w-full flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 px-2 py-1 text-left"
                                      onClick={() => setExpandedRoom({ ...expandedRoom, [rk]: !l3Open })} data-testid={`room-toggle-${rk}`}>
                                      <span className="flex items-center gap-2 w-full sm:flex-1 min-w-0">
                                        {l3Open ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
                                        <span className="min-w-0 truncate text-xs font-semibold text-slate-600">{lvl.lab3(l3)}</span>
                                      </span>
                                      <span className="pl-5 sm:pl-0 flex items-center gap-1">
                                        <CountBadges items={l3Items} />
                                        {canManage && !byTown && (
                                          <span role="button" data-testid={`flag-room-${rk}`} className="text-emerald-600 p-1 -m-1 shrink-0"
                                            onClick={(e) => { e.stopPropagation(); setReadyDialog({ townhouseId: l2, room: l3, dwellingLabel: lvl.lab2(l2) }); }}>
                                            <Flag className="w-4 h-4" />
                                          </span>
                                        )}
                                        {canManage && !byTown && (
                                          <span role="button" data-testid={`edit-room-${rk}`} className="text-slate-500 p-1 -m-1 shrink-0"
                                            onClick={(e) => { e.stopPropagation(); setRoomEdit({ room: l3, townhouseId: l2, dwellingLabel: lvl.lab2(l2), newName: l3 }); }}>
                                            <Pencil className="w-4 h-4" />
                                          </span>
                                        )}
                                        {canManage && !byTown && (
                                          <span role="button" data-testid={`del-room-${rk}`} className="text-red-500 p-1 -m-1 shrink-0"
                                            onClick={(e) => { e.stopPropagation(); setRoomDelete({ room: l3, townhouseId: l2, dwellingLabel: lvl.lab2(l2) }); }}>
                                            <Trash2 className="w-4 h-4" />
                                          </span>
                                        )}
                                      </span>
                                    </button>
                                    {l3Open && (
                                      <div className="px-2 pb-1">
                                        {[...l3Items].filter(isVisible).sort((a, b) => (STATUS_ORDER[a.status] - STATUS_ORDER[b.status]) || sortNat(a.item_name, b.item_name)).map((it) => (
                                          <div key={it.id} className="border-t" data-testid={`item-wrap-${it.id}`}>
                                          <div className="flex flex-wrap items-center gap-2 py-1 text-sm" data-testid={`item-row-${it.id}`}>
                                            {it.status === "complete" && <Checkbox checked={!!selected[it.id]} onCheckedChange={(v) => setSelected({ ...selected, [it.id]: !!v })} />}
                                            <span className="flex-1 min-w-[55%] break-words">
                                              {it.item_name}
                                              {it.marked_complete_by && <span className="text-xs text-slate-400"> — by {it.marked_complete_by}</span>}
                                              {fmtDate(it.marked_complete_at) && <span className="text-xs text-slate-400"> · completed {fmtDate(it.marked_complete_at)}</span>}
                                            </span>
                                            {(it.history || []).length > 0 && (
                                              <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-500" data-testid={`history-${it.id}`}
                                                onClick={() => setOpenHistory((o) => ({ ...o, [it.id]: !o[it.id] }))}><History className="w-3.5 h-3.5" /></Button>
                                            )}
                                            {it.status !== "not_started" && <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLE[it.status]}`}>{STATUS_LABEL[it.status]}</span>}
                                            {it.status === "not_started" && it.ready_to_start && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold" data-testid={`ready-badge-${it.id}`}>Ready to start</span>}
                                            {it.status === "not_started" && (
                                              <Button size="sm" variant="ghost" className={`h-7 px-2 ${(it.notes || []).length ? "text-amber-600" : "text-slate-400"}`} data-testid={`notes-btn-${it.id}`} onClick={() => setNotesItem(it)}>
                                                <MessageSquare className="w-3.5 h-3.5" />{(it.notes || []).length > 0 && <span className="ml-1 text-xs font-semibold">{it.notes.length}</span>}
                                              </Button>
                                            )}
                                            {it.status === "not_started" && (
                                              <Select value={String(it.progress || 0)} onValueChange={(v) => setProgress(it.id, v)}>
                                                <SelectTrigger className="h-8 w-[150px]" data-testid={`progress-${it.id}`}><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                  {PROGRESS_OPTS.map((p) => <SelectItem key={p} value={p}>{p}%</SelectItem>)}
                                                  <SelectItem value="100">Mark Complete (100%)</SelectItem>
                                                </SelectContent>
                                              </Select>
                                            )}
                                            {it.status === "complete" && <Button size="sm" variant="outline" data-testid={`undo-${it.id}`} onClick={() => undoComplete(it.id)}><Undo2 className="w-3.5 h-3.5 mr-1" />Undo</Button>}
                                            {it.status !== "closed_out" && <Button size="sm" variant="outline" data-testid={`closeout-${it.id}`} onClick={() => closeOutOne(it)}>Close Out</Button>}
                                            {canDelete && it.status !== "closed_out" && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => delItem(it.id)}><Trash2 className="w-4 h-4" /></Button>}
                                          </div>
                                          {openHistory[it.id] && (
                                            <div className="ml-2 mb-2 pl-3 border-l-2 border-slate-200 space-y-1" data-testid={`history-panel-${it.id}`}>
                                              {[...(it.history || [])].reverse().map((h, hi) => (
                                                <div key={hi} className="text-xs text-slate-500">
                                                  <span className="font-medium text-slate-700">{h.label}</span> — {h.by} · {fmtDateTime(h.at)}
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {showApply && <ApplyDialog projectId={projectId} templates={templates} dwellings={dwellings} existingTh={data.townhouses || []}
        onClose={() => setShowApply(false)} onDone={() => { setShowApply(false); load(projectId); }} />}
      {showAdHoc && <AdHocDialog projectId={projectId} subcontractors={subcontractors} dwellings={dwellings}
        onClose={() => setShowAdHoc(false)} onDone={() => { setShowAdHoc(false); load(projectId); }} />}
      {showTokens && <TokensDialog projectId={projectId} subcontractors={subcontractors} tokens={tokens}
        onClose={() => setShowTokens(false)} reload={() => load(projectId)} />}
      {showActivity && <ActivityDialog projectId={projectId} onClose={() => setShowActivity(false)} />}
      {roomDelete && (
        <Dialog open onOpenChange={() => setRoomDelete(null)}>
          <DialogContent className="max-w-md" data-testid="room-delete-dialog">
            <DialogHeader><DialogTitle>Delete room "{roomDelete.room}"?</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600">
              This permanently deletes <span className="font-semibold">all subcontractors and items</span> for this room
              (including any already closed out). This cannot be undone.
            </p>
            <div className="space-y-2 mt-2">
              <Button variant="destructive" className="w-full" data-testid="del-room-dwelling"
                onClick={() => deleteRoom(false)}>
                Delete in {roomDelete.dwellingLabel} only
              </Button>
              <Button variant="destructive" className="w-full" data-testid="del-room-all"
                onClick={() => deleteRoom(true)}>
                Delete across ALL townhouses
              </Button>
              <Button variant="outline" className="w-full" onClick={() => setRoomDelete(null)}>Cancel</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {roomEdit && (
        <Dialog open onOpenChange={() => setRoomEdit(null)}>
          <DialogContent className="max-w-md" data-testid="room-rename-dialog">
            <DialogHeader><DialogTitle>Rename room "{roomEdit.room}"</DialogTitle></DialogHeader>
            <Input autoFocus data-testid="room-rename-input" value={roomEdit.newName}
              onChange={(e) => setRoomEdit({ ...roomEdit, newName: e.target.value })} placeholder="New room name" />
            <p className="text-xs text-slate-500">Renaming updates the room label on all its items.</p>
            <div className="space-y-2 mt-2">
              <Button className="w-full" data-testid="rename-room-dwelling"
                disabled={!roomEdit.newName.trim() || roomEdit.newName.trim() === roomEdit.room}
                onClick={() => renameRoom(false)}>Rename in {roomEdit.dwellingLabel} only</Button>
              <Button className="w-full" data-testid="rename-room-all"
                disabled={!roomEdit.newName.trim() || roomEdit.newName.trim() === roomEdit.room}
                onClick={() => renameRoom(true)}>Rename across ALL townhouses</Button>
              <Button variant="outline" className="w-full" onClick={() => setRoomEdit(null)}>Cancel</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {readyDialog && (
        <ReadyFlagDialog ctx={readyDialog} items={items} dwName={dwName}
          onClose={() => setReadyDialog(null)} onFlag={flagReady} />
      )}
      {notesItem && (
        <NotesDialog item={items.find((i) => i.id === notesItem.id) || notesItem} canEdit={canManage}
          onClose={() => setNotesItem(null)}
          onAdd={(t) => addNote(notesItem.id, t)}
          onEdit={(nid, t) => editNote(notesItem.id, nid, t)}
          onDelete={(nid) => deleteNote(notesItem.id, nid)} />
      )}
    </div>
  );
}

function NotesDialog({ item, canEdit, onClose, onAdd, onEdit, onDelete }) {
  const [text, setText] = useState("");
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const notes = item.notes || [];
  const roleStyle = (r) => r === "manager" ? "border-blue-300 bg-blue-50" : "border-emerald-300 bg-emerald-50";
  const roleLabel = (r) => r === "manager" ? "Site Manager" : "Subcontractor";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="notes-dialog">
        <DialogHeader><DialogTitle>Notes · {item.item_name}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {notes.length === 0 && <p className="text-sm text-slate-400">No notes yet.</p>}
          {notes.map((n) => (
            <div key={n.id} className={`border rounded-md p-2 ${roleStyle(n.role)}`} data-testid={`note-${n.id}`}>
              {editId === n.id ? (
                <div className="space-y-1">
                  <Textarea maxLength={250} value={editText} onChange={(e) => setEditText(e.target.value)} data-testid="note-edit-input" />
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="outline" onClick={() => setEditId(null)}>Cancel</Button>
                    <Button size="sm" onClick={async () => { if (await onEdit(n.id, editText.trim())) setEditId(null); }}>Save</Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">{n.text}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${n.role === "manager" ? "bg-blue-200 text-blue-800" : "bg-emerald-200 text-emerald-800"}`}>{roleLabel(n.role)}</span>
                    <span className="text-xs text-slate-500 flex-1 min-w-0 truncate">{n.by} · {fmtDateTime(n.at)}{n.edited_at ? " (edited)" : ""}</span>
                    {canEdit && (
                      <>
                        <button className="text-slate-500" data-testid={`note-edit-${n.id}`} onClick={() => { setEditId(n.id); setEditText(n.text); }}><Pencil className="w-3.5 h-3.5" /></button>
                        <button className="text-red-500" data-testid={`note-del-${n.id}`} onClick={() => onDelete(n.id)}><Trash2 className="w-3.5 h-3.5" /></button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
        <div className="mt-2 space-y-1">
          <Textarea maxLength={250} placeholder="Add a note (max 250 characters)…" value={text}
            onChange={(e) => setText(e.target.value)} data-testid="note-add-input" />
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">{text.length}/250</span>
            <Button size="sm" data-testid="note-add-submit" disabled={!text.trim()}
              onClick={async () => { if (await onAdd(text.trim())) setText(""); }}>Add note</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReadyFlagDialog({ ctx, items, dwName, onClose, onFlag }) {
  const [sel, setSel] = useState({});
  const [note, setNote] = useState("");
  const candidates = items.filter((i) =>
    i.townhouse_id === ctx.townhouseId &&
    (i.room_instance_label || i.room_name) === ctx.room &&
    i.status === "not_started" && (i.progress || 0) === 0 &&
    (!ctx.excludeSubId || i.subcontractor_id !== ctx.excludeSubId));
  const bySub = {};
  candidates.forEach((i) => { (bySub[i.subcontractor_name || "Unassigned"] = bySub[i.subcontractor_name || "Unassigned"] || []).push(i); });
  const chosen = Object.keys(sel).filter((k) => sel[k]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="ready-flag-dialog">
        <DialogHeader><DialogTitle>Ready to start — {ctx.dwellingLabel} · {ctx.room}</DialogTitle></DialogHeader>
        {candidates.length === 0 ? (
          <p className="text-sm text-slate-400">No other trades have pending items in this room.</p>
        ) : (
          <>
            <p className="text-sm text-slate-600">Tick the item(s) a following trade can now start:</p>
            <div className="space-y-3 mt-1">
              {Object.keys(bySub).sort().map((sub) => (
                <div key={sub}>
                  <p className="text-sm font-semibold text-slate-700">{sub}</p>
                  {bySub[sub].map((i) => (
                    <label key={i.id} className="flex items-center gap-2 py-1 text-sm" data-testid={`ready-cand-${i.id}`}>
                      <Checkbox checked={!!sel[i.id]} onCheckedChange={(v) => setSel({ ...sel, [i.id]: !!v })} />
                      {i.item_name}
                    </label>
                  ))}
                </div>
              ))}
            </div>
            <Input className="mt-2" placeholder="Optional note (e.g. Benchtop installed)" value={note}
              onChange={(e) => setNote(e.target.value)} data-testid="ready-note-input" />
          </>
        )}
        <div className="flex gap-2 mt-3">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          {candidates.length > 0 && (
            <Button className="flex-1" data-testid="ready-flag-submit" disabled={chosen.length === 0}
              onClick={() => onFlag(chosen, note.trim() || null)}>Flag ready ({chosen.length})</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ActivityDialog({ projectId, onClose }) {
  const [feed, setFeed] = useState(null);
  useEffect(() => {
    fetch(`${API}/fitoff/${projectId}/activity?limit=100`, { headers: authHeaders() })
      .then((r) => r.json()).then((d) => setFeed(d.activity || []));
  }, [projectId]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="activity-dialog">
        <DialogHeader><DialogTitle>Recent activity</DialogTitle></DialogHeader>
        {feed === null && <p className="text-sm text-slate-400">Loading…</p>}
        {feed && feed.length === 0 && <p className="text-sm text-slate-400">No activity yet.</p>}
        <div className="space-y-2">
          {feed && feed.map((e, i) => (
            <div key={i} className="border-l-2 border-blue-200 pl-3 py-1" data-testid={`activity-row-${i}`}>
              <p className="text-sm text-slate-800"><span className="font-semibold">{e.label}</span> · {e.item_name}</p>
              <p className="text-xs text-slate-500">{e.dwelling} → {e.room} → {e.subcontractor} · by {e.by} · {fmtDateTime(e.at)}</p>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ApplyDialog({ projectId, templates, dwellings, existingTh, onClose, onDone }) {
  const [templateId, setTemplateId] = useState("");
  // Default: every dwelling ticked.
  const [selDw, setSelDw] = useState(() => Object.fromEntries((dwellings || []).map((d) => [d.id, true])));
  const [beds, setBeds] = useState({});
  const existingIds = new Set((existingTh || []).map((t) => t.townhouse_id));
  const siteTemplates = templates.filter((t) => t.site_id === projectId);
  const allSelected = dwellings.length > 0 && dwellings.every((d) => selDw[d.id]);
  const toggleAll = () => {
    const v = !allSelected;
    setSelDw(Object.fromEntries(dwellings.map((d) => [d.id, v])));
  };

  const apply = async () => {
    if (!templateId) return toast.error("Pick a template");
    const ids = Object.keys(selDw).filter((k) => selDw[k]);
    if (ids.length === 0) return toast.error("Select at least one townhouse");
    const bedroom_counts = {};
    ids.forEach((id) => { if (!existingIds.has(id)) bedroom_counts[id] = parseInt(beds[id] || "1", 10); });
    const res = await fetch(`${API}/fitoff/${projectId}/apply-template`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({ template_id: templateId, townhouse_ids: ids, bedroom_counts }) });
    if (res.ok) { const d = await res.json(); toast.success(`${d.items_created} items generated`); onDone(); }
    else toast.error("Apply failed");
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto" data-testid="apply-dialog">
        <DialogHeader><DialogTitle>Apply Template</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Master Template</Label>
            <Select value={templateId} onValueChange={setTemplateId}>
              <SelectTrigger data-testid="apply-template-select"><SelectValue placeholder="Choose template" /></SelectTrigger>
              <SelectContent>
                {siteTemplates.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">No templates for this site yet — create one in Master Templates.</div>}
                {siteTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Townhouses</Label>
              {dwellings.length > 0 && (
                <button type="button" className="text-xs font-medium text-orange-600" data-testid="apply-select-all" onClick={toggleAll}>
                  {allSelected ? "Deselect all" : "Select all"}
                </button>
              )}
            </div>
            {dwellings.length === 0 && <p className="text-xs text-slate-400">No townhouses/dwellings on this project yet — add them in Defects first.</p>}
            <div className="border rounded-lg p-2 max-h-64 overflow-y-auto space-y-1">
              {dwellings.map((d) => (
                <div key={d.id} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={!!selDw[d.id]} onCheckedChange={(v) => setSelDw({ ...selDw, [d.id]: !!v })} data-testid={`apply-dw-${d.id}`} />
                  <span className="flex-1">{d.dwelling_number}</span>
                  {selDw[d.id] && !existingIds.has(d.id) && (
                    <span className="flex items-center gap-1 text-xs">bedrooms
                      <Input className="h-7 w-16" type="number" min="1" value={beds[d.id] || ""} placeholder="4"
                        onChange={(e) => setBeds({ ...beds, [d.id]: e.target.value })} data-testid={`apply-beds-${d.id}`} /></span>
                  )}
                  {existingIds.has(d.id) && <span className="text-xs text-slate-400">already set</span>}
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-1">Bedroom count includes the Master Bedroom (e.g. 4 = 1 Master + 3 Bedrooms).</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="apply-confirm-btn" onClick={apply}>Generate Items</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdHocDialog({ projectId, subcontractors, dwellings, onClose, onDone }) {
  const [f, setF] = useState({ townhouse_id: "", subcontractor_id: "", room_name: "", room_instance_number: "", item_name: "", retro: false });
  const siteSubs = subcontractors.filter((t) => (t.site_ids || []).includes(projectId));
  const save = async () => {
    if (!f.townhouse_id || !f.subcontractor_id || !f.room_name || !f.item_name) return toast.error("Fill townhouse, subcontractor, room and item");
    const tr = subcontractors.find((t) => t.id === f.subcontractor_id);
    const body = {
      townhouse_id: f.townhouse_id, subcontractor_id: f.subcontractor_id, subcontractor_name: tr?.name || "",
      room_name: f.room_name, item_name: f.item_name,
      room_instance_number: f.room_instance_number ? parseInt(f.room_instance_number, 10) : null,
      retro_add_to_template: f.retro,
    };
    const res = await fetch(`${API}/fitoff/${projectId}/items`, { method: "POST", headers: authHeaders(), body: JSON.stringify(body) });
    if (res.ok) { const d = await res.json(); toast.success(f.retro ? `Item added + back-filled to ${d.back_filled} lot(s)` : "Item added"); onDone(); }
    else toast.error("Add failed");
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px] max-h-[90vh] overflow-y-auto" data-testid="adhoc-dialog">
        <DialogHeader><DialogTitle>Add Ad Hoc Item</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Townhouse</Label>
            <Select value={f.townhouse_id} onValueChange={(v) => setF({ ...f, townhouse_id: v })}>
              <SelectTrigger data-testid="adhoc-townhouse"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{dwellings.map((d) => <SelectItem key={d.id} value={d.id}>{d.dwelling_number}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Subcontractor</Label>
            <Select value={f.subcontractor_id} onValueChange={(v) => setF({ ...f, subcontractor_id: v })}>
              <SelectTrigger data-testid="adhoc-subcontractor"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {siteSubs.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">No subcontractors on this site.</div>}
                {siteSubs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div><Label>Room</Label><Input data-testid="adhoc-room" value={f.room_name} onChange={(e) => setF({ ...f, room_name: e.target.value })} placeholder="e.g. Kitchen, Bedroom" /></div>
          <div><Label>Room instance # (optional, for repeatable rooms)</Label><Input type="number" value={f.room_instance_number} onChange={(e) => setF({ ...f, room_instance_number: e.target.value })} placeholder="e.g. 2 for Bedroom 2" /></div>
          <div><Label>Item</Label><Input data-testid="adhoc-item" value={f.item_name} onChange={(e) => setF({ ...f, item_name: e.target.value })} placeholder="e.g. Carpet" /></div>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.retro} onCheckedChange={(v) => setF({ ...f, retro: !!v })} data-testid="adhoc-retro" /> Also add this item to the project template (back-fills every other townhouse)</label>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button data-testid="adhoc-save-btn" onClick={save}>Add Item</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TokensDialog({ projectId, subcontractors, tokens, onClose, reload }) {
  const [subcontractorId, setSubcontractorId] = useState("");
  const origin = window.location.origin;
  const siteSubs = subcontractors.filter((t) => (t.site_ids || []).includes(projectId));
  const gen = async () => {
    if (!subcontractorId) return toast.error("Pick a subcontractor");
    const res = await fetch(`${API}/fitoff/${projectId}/subcontractor-token`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ subcontractor_id: subcontractorId }) });
    if (res.ok) { toast.success("Link generated"); setSubcontractorId(""); reload(); } else toast.error("Failed");
  };
  const revoke = async (id) => { const res = await fetch(`${API}/fitoff/${projectId}/subcontractor-token/${id}/revoke`, { method: "POST", headers: authHeaders() }); if (res.ok) { toast.success("Revoked"); reload(); } };
  const copy = (t) => { navigator.clipboard.writeText(`${origin}/fitoff/${t}`); toast.success("Link copied"); };
  const active = tokens.filter((t) => t.active);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto" data-testid="tokens-dialog">
        <DialogHeader><DialogTitle>Subcontractor Portal Links</DialogTitle></DialogHeader>
        <div className="flex gap-2 items-end mb-3">
          <div className="flex-1"><Label>Generate link for subcontractor</Label>
            <Select value={subcontractorId} onValueChange={setSubcontractorId}>
              <SelectTrigger data-testid="token-subcontractor-select"><SelectValue placeholder="Select subcontractor" /></SelectTrigger>
              <SelectContent>
                {siteSubs.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-400">No subcontractors on this site.</div>}
                {siteSubs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <Button data-testid="gen-token-btn" onClick={gen}>Generate</Button>
        </div>
        <div className="space-y-3">
          {active.length === 0 && <p className="text-xs text-slate-400">No active links.</p>}
          {active.map((t) => {
            const url = `${origin}/fitoff/${t.token}`;
            return (
              <div key={t.id} className="flex items-center gap-3 border rounded p-2 text-sm" data-testid={`token-row-${t.id}`}>
                <div className="bg-white p-1 rounded border shrink-0">
                  <QRCodeCanvas id={`qr-${t.id}`} value={url} size={72} level="M" includeMargin={false} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{t.subcontractor_name}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <Button size="sm" variant="outline" onClick={() => copy(t.token)} data-testid={`token-copy-${t.id}`}><Copy className="w-3 h-3 mr-1" />Copy link</Button>
                    <Button size="sm" variant="outline" onClick={() => downloadQR(t.id, t.subcontractor_name)} data-testid={`token-qr-${t.id}`}><QrCode className="w-3 h-3 mr-1" />Save QR</Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => revoke(t.id)}><Ban className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
